<template>
  <layout-person v-if="judge" :color="color">
    <template v-slot:header>
      <!-- Portrait -->
      <candidate-profile-top class="relative pb-6 lg:pb-0">
        <div class="profile-picture pointer-events-none">
          <img
            :src="judge.urlImage || fallBackImage"
            draggable="false"
            alt=""
            v-fallback="fallBackImage"
          />
        </div>
      </candidate-profile-top>
    </template>
    <!-- Content -->
    <template v-slot:information>
      <h1
        class="text-chplus-title-l font-bold mx-auto mt-2 mb-2"
        style="text-overflow: ellipsis;max-width: 95%;"
        :style="{ color: color }"
      >
        {{ fullName }}
      </h1>
      <div class="match-progress"></div>
      <p class="text-chplus-l text-interaction-dark mt-4">
        {{ judge.roleTitle }}
      </p>
      <p
        v-if="judge.additionalRoleTitle"
        class="text-chplus-regular text-interaction-dark mb-0"
      >
        {{ judge.additionalRoleTitle }}
      </p>
      <div
        class="candidate-info-box pointer-events-none flex flex-col justify-center candidate-info-box-l"
      >
        <div
          class="candidate-info-container flex flex-col flex-grow justify-between text-chplus-candidate-info text-color-candidate"
        >
          <birthday :candidate="judge" />
          <city :candidate="judge" />
        </div>
      </div>
    </template>
    <template v-slot:tabs>
      <!-- Tabs -->
      <content-tabs class="pointer-events-none">
        <content-tab currentTab="profile" :color="color" tabName="profile">
          <icon-smiley />
        </content-tab>
      </content-tabs>
    </template>
    <!-- Tab Bodys -->
    <template v-slot:content>
      <tab
        activeTab="profile"
        :color="color"
        tabName="profile"
        :title="$t('views.single.judge.biography_title')"
      >
        <p
          class="mb-5 text-justify whitespace-pre-line text-chplus-candidate-info text-color-candidate lg:text-xl lg:text-left"
        >
          {{ judge.bio }}
        </p>
      </tab>
      <div class="flex justify-center mt-10 mb-3">
        <a
          :href="judge.bioUrl"
          target="_blank"
          class="w-fit rounded-lg p-3 bg-interaction-dark text-white"
        >
          {{ $t("views.single.judge.link_profile") }}
          <icon-link-profile />
        </a>
      </div>
    </template>
  </layout-person>
</template>

<script>
import { mapGetters } from "vuex";
import { randomSample } from "@/utils/ArrayUtility";
import ShareLinkMixin from "@/utils/ShareLinkMixin";
import IsMobileMixin from "../../utils/IsMobileMixin";

import LayoutPerson from "@/components/layouts/LayoutPerson";
import CandidateProfileTop from "@/components/layouts/CandidateProfileTop";
import FallbackImg from "@/directives/FallbackImg";
import client from "api-client";
import { LanguageCode } from "../../utils/LanguageCode";
import ContentTabs from "@/components/nav/ContentTabs";
import ContentTab from "@/components/nav/ContentTab";
import Tab from "@/components/people/Tab";
import IconSmiley from "@/components/icons/IconSmiley.vue";
import Birthday from "@/components/people/profile/Birthday";
import City from "@/components/people/profile/City";
import IconLinkProfile from "../../components/icons/IconLinkProfile";

export default {
  name: "Councillor",
  directives: {
    fallback: FallbackImg
  },
  mixins: [ShareLinkMixin, IsMobileMixin],
  components: {
    CandidateProfileTop,
    LayoutPerson,
    IconSmiley,
    ContentTabs,
    ContentTab,
    Tab,
    Birthday,
    City,
    IconLinkProfile
  },
  props: {
    id: {
      type: [String, Number],
      required: true
    }
  },
  data: () => ({
    judge: null
  }),
  async created() {
    this.judge = await client.fetchJudge(
      Number(this.id),
      LanguageCode.get(this.contentLanguage)
    );
  },
  computed: {
    ...mapGetters("user", ["contentLanguage"]),
    ...mapGetters("locale", ["locale"]),
    fullName() {
      return `${this.judge.firstName} ${this.judge.lastName.toUpperCase()}`;
    },
    color() {
      return "#888EA8";
    },
    fallBackImage() {
      if (this.judge.gender === "m") {
        return require("@/assets/images/genicon-judge-male.svg");
      } else if (this.judge.gender === "f") {
        return require("@/assets/images/genicon-judge-female.svg");
      } else {
        return randomSample([
          require("@/assets/images/genicon-judge-male.svg"),
          require("@/assets/images/genicon-judge-female.svg")
        ]);
      }
    }
  },
  methods: {
    isNullOrWhitespace(value) {
      return !(value != null && value.toString().trim() !== "");
    },
    share() {
      this.shareCurrentLink(
        this.$t("views.single.judge.share_subject"),
        this.$t("views.single.judge.share_message", {
          name: this.fullName
        })
      );
    }
  }
};
</script>

<style lang="scss" scoped>
.profile-picture,
.smart-spider-box {
  width: 10.625rem;
  height: 10.625rem;
  flex: 0 0 10.625rem;
}
.profile-picture {
  position: relative;

  box-shadow: rgba(255, 238, 220, 0.2) 0 0 40px 10px;
  border-radius: 50%;
  img {
    position: absolute;
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: 50%;
    margin: 0 auto;
  }
  &::after {
    content: "";
    display: block;
    padding-bottom: 100%;
  }
}

.section {
  margin: 1.5rem 0;
  &:first-child {
    margin-top: -0.5rem;
  }
}

.match-progress {
  width: 95%;
  height: 0.3125rem;
  margin-left: auto;
  margin-right: auto;
  background-color: var(--interaction-light);
  border-radius: 0.15625rem;
}

.match-progress div {
  height: 100%;
  border-radius: 0.15625rem;
}

@media screen and (min-width: 768px) {
  .profile-picture,
  .smart-spider-box {
    width: 12.0625rem;
    height: 12.0625rem;
    flex: 0 0 12.0625rem;
  }

  .match-progress {
    width: 70%;
  }
}
</style>
